import {
  ActionIcon,
  Badge,
  Button,
  Card,
  FileButton,
  Group,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconCirclePlus, IconCloudUpload, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';

/** Do-not-contact list — the "Do-not-contact" tab of the Campaigns hub. Numbers
 *  here are skipped by every campaign send. */
export function SuppressionPanel() {
  const list = trpc.campaigns.suppression.list.useQuery();
  const utils = trpc.useUtils();
  const add = trpc.campaigns.suppression.add.useMutation({
    onSuccess: () => {
      void utils.campaigns.suppression.list.invalidate();
      notifications.show({ message: 'Added', color: 'green' });
    },
  });
  const addCsv = trpc.campaigns.suppression.addFromCsv.useMutation({
    onSuccess: (r) => {
      void utils.campaigns.suppression.list.invalidate();
      notifications.show({
        message: `Added ${r.added} (skipped ${r.skipped})`,
        color: 'green',
      });
    },
  });
  const remove = trpc.campaigns.suppression.remove.useMutation({
    onSuccess: () => void utils.campaigns.suppression.list.invalidate(),
  });
  const [waId, setWaId] = useState('');
  const [reason, setReason] = useState('');

  async function handleCsv(f: File | null) {
    if (!f) return;
    const text = await f.text();
    addCsv.mutate({ csv: text });
  }

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        Numbers on this list are skipped by every campaign send.
      </Text>

      <Card>
        <Stack gap="sm">
          <Title order={5}>Add manually</Title>
          <Group>
            <TextInput
              flex={1}
              label="WhatsApp ID"
              placeholder="+91 9272723772"
              value={waId}
              onChange={(e) => setWaId(e.currentTarget.value)}
            />
            <TextInput
              flex={2}
              label="Reason"
              placeholder="Customer requested removal"
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
            />
            <Button
              mt="lg"
              leftSection={<IconCirclePlus size={14} />}
              onClick={() => {
                if (!waId.trim()) return;
                add.mutate({ waId, reason: reason || null });
                setWaId('');
                setReason('');
              }}
            >
              Add
            </Button>
          </Group>
          <Group>
            <FileButton onChange={handleCsv} accept=".csv,text/plain,text/csv">
              {(props) => (
                <Button {...props} variant="default" leftSection={<IconCloudUpload size={14} />}>
                  Bulk import CSV
                </Button>
              )}
            </FileButton>
            <Text c="dimmed" size="sm">
              One number per line. Optional reason after a comma. Lines starting with <code>#</code>{' '}
              are ignored.
            </Text>
          </Group>
        </Stack>
      </Card>

      <Card p={0}>
        {list.isPending ? (
          <Skeleton height={200} m="md" />
        ) : (
          <Table striped highlightOnHover withRowBorders={false}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>WhatsApp ID</Table.Th>
                <Table.Th>Reason</Table.Th>
                <Table.Th>Source</Table.Th>
                <Table.Th>Added</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(list.data ?? []).map((s) => (
                <Table.Tr key={s.id}>
                  <Table.Td>
                    <Text ff="monospace" size="sm">
                      +{s.waId}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c={s.reason ? undefined : 'dimmed'}>
                      {s.reason ?? '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="xs" variant="light">
                      {s.source}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {new Date(s.createdAt).toLocaleDateString()}
                    </Text>
                  </Table.Td>
                  <Table.Td align="right">
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      onClick={() => remove.mutate({ id: s.id })}
                      aria-label="Remove"
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
              {(list.data ?? []).length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text c="dimmed" ta="center" py="md">
                      No entries yet.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        )}
      </Card>
    </Stack>
  );
}
